import { useEffect, useRef, useState } from 'react';
import {
  isTerminalSyncStatus,
  statusFromApiResponse,
} from 'screens/Admin/Accounts/components/syncSolicitudDocModalHelpers';

const POLL_MS = 3500;
const MAX_ATTEMPTS = 100;

type UseSyncSolicitudDocStatusPollingParams = {
  accountId: number;
  runId: string;
  enabled: boolean;
  getSyncStatusAction: (
    id: number,
    opts?: { runId?: string; silent?: boolean },
  ) => Promise<any>;
  onTerminal: ( response: any ) => void;
  onTimeout: () => void;
};

function useSyncSolicitudDocStatusPolling( {
  accountId,
  runId,
  enabled,
  getSyncStatusAction,
  onTerminal,
  onTimeout,
}: UseSyncSolicitudDocStatusPollingParams ) {
  const [lastResponse, setLastResponse] = useState<any>( null );
  const onTerminalRef = useRef( onTerminal );
  const onTimeoutRef = useRef( onTimeout );
  onTerminalRef.current = onTerminal;
  onTimeoutRef.current = onTimeout;

  useEffect( () => {
    if ( !enabled || !runId ) return undefined;

    let cancelled = false;
    let attempts = 0;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      if ( cancelled ) return;
      attempts += 1;
      if ( attempts > MAX_ATTEMPTS ) {
        onTimeoutRef.current();
        return;
      }
      const res = await getSyncStatusAction( accountId, { runId, silent: true } );
      if ( cancelled ) return;
      setLastResponse( res );
      const st = statusFromApiResponse( res );
      if ( res?.ok && isTerminalSyncStatus( st ) ) {
        onTerminalRef.current( res );
        return;
      }
      if ( cancelled ) return;
      timeoutId = setTimeout( () => {
        tick().catch( () => {} );
      }, POLL_MS );
    };

    tick().catch( () => {} );

    return () => {
      cancelled = true;
      if ( timeoutId !== undefined ) clearTimeout( timeoutId );
    };
  }, [accountId, enabled, getSyncStatusAction, runId] );

  return { lastResponse };
}

export default useSyncSolicitudDocStatusPolling;
